import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Comments() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">💬 JavaScript Comments</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Comments are text notes you can add to your code to explain what it
        does. They are ignored by the JavaScript engine and don’t affect how
        your program runs. Comments make your code easier to read and maintain.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
        }}
      >
        <h2 style={{ color: "#166534" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Comments are <b>ignored</b> during execution.
          </li>
          <li>Use them to explain logic, steps, or complex code.</li>
          <li>
            They make your code more <b>readable and maintainable</b>.
          </li>
          <li>
            JavaScript supports <b>single-line</b> and <b>multi-line</b>{" "}
            comments.
          </li>
        </ul>
      </div>

      {/* Example 1: Single-Line Comments */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Single-Line Comments</h2>
        <p>
          Use <code>//</code> for single-line comments. Everything after{" "}
          <code>//</code> on that line is ignored.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`// This is a single-line comment
let x = 5; // This comment is after code
console.log(x);`}
          />
          <TryItButton
            code={`// This is a single-line comment
let x = 5; // This comment is after code
console.log(x);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output: <b>5</b>
        </div>
      </div>

      {/* Example 2: Multi-Line Comments */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Multi-Line Comments</h2>
        <p>
          Use <code>/* ... */</code> for comments that span multiple lines.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`/*
 This is a multi-line comment
 It can span several lines
*/
let y = 10;
console.log(y);`}
          />
          <TryItButton
            code={`/*
 This is a multi-line comment
 It can span several lines
*/
let y = 10;
console.log(y);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output: <b>10</b>
        </div>
      </div>

      {/* Example 3: Commenting Out Code */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Commenting Out Code</h2>
        <p>
          You can use comments to temporarily disable code without deleting it.
          This is helpful for debugging or testing.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = 10;
let b = 20;

// console.log(a + b); // This line is commented out
console.log("Code still runs fine!");`}
          />
          <TryItButton
            code={`let a = 10;
let b = 20;

// console.log(a + b); // This line is commented out
console.log("Code still runs fine!");`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output: <b>Code still runs fine!</b>
        </div>
      </div>

      {/* Example 4: Inline Commenting */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Inline Commenting</h2>
        <p>
          You can write comments on the same line as your code to explain what
          it does.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let price = 100; // original price
let discount = 20; // discount in percentage
let finalPrice = price - (price * discount / 100); // applying discount
console.log(finalPrice);`}
          />
          <TryItButton
            code={`let price = 100; // original price
let discount = 20; // discount in percentage
let finalPrice = price - (price * discount / 100); // applying discount
console.log(finalPrice);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          Output: <b>80</b>
        </div>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>Keep comments concise but meaningful.</li>
          <li>Use comments to explain “why”, not “what”.</li>
          <li>Don’t overuse comments — write clear, self-explanatory code.</li>
          <li>Remove outdated comments when the code changes.</li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>Variables</b> and how to store values in JavaScript.
          </li>
          <li>
            Understand <b>Data Types</b> and <b>Operators</b>.
          </li>
          <li>
            Practice writing <b>simple programs</b> with comments.
          </li>
        </ul>
      </div>
    </div>
  );
}
